import type { PerformancePart, Scale } from '@tunasong/schemas'
import { createContext, useContext } from 'react'

/**
 * What the performer's parts need on screen: the UNION of what each chosen part needs (spec "Combining
 * parts"). Editor plugins read these flags, never the parts themselves, so a new part or a rule change
 * lives in `partFeatures` alone. Plugins change only how things render, never which nodes exist (D3).
 */
export interface PartFeatures {
  /** Guitar, Bass, Keys or Full chart chosen, or "Show chords" on. */
  chordsShown: boolean
  /** Drums with chords hidden: a no-chord (N.C.) renders as STOP. */
  stopForNoChord: boolean
  /** Vocals: lyrics larger, chords kept on their syllable. */
  largeLyrics: boolean
  /** Vocals: backing lines, cues and dynamics at full contrast (cues are never hidden). */
  vocalCues: boolean
  /** Drums without Vocals: lyrics dimmed, never hidden. */
  dimLyrics: boolean
  /** Drums or Bass: bar counts, odd-meter badges, hits and stops. */
  structure: boolean
  /** Drums: the tempo segment of the header line (the metronome already shows tempo to others, D15). */
  tempoLine: boolean
  /** Drums or Vocals: the count-in segment of the header line. */
  countIn: boolean
  /**
   * No chosen part shows chords (D16): instrumental sections collapse to one line with their bar count,
   * which is what a singer or a drummer needs instead of blank chord-only lines.
   */
  collapseInstrumental: boolean
  /** Guitar: tabs expanded. */
  tabsExpanded: boolean
}

const chordParts: readonly PerformancePart[] = ['guitar', 'bass', 'keys', 'full']

export const partFeatures = (parts: readonly PerformancePart[], options: { showChords: boolean }): PartFeatures => {
  const has = (part: PerformancePart) => parts.includes(part)
  const vocals = has('vocals')
  const drums = has('drums')
  const chordsShown = parts.some(part => chordParts.includes(part)) || options.showChords
  return {
    chordsShown,
    stopForNoChord: drums && !chordsShown,
    largeLyrics: vocals,
    vocalCues: vocals,
    dimLyrics: drums && !vocals,
    structure: drums || has('bass'),
    tempoLine: drums,
    countIn: drums || vocals,
    collapseInstrumental: !chordsShown,
    tabsExpanded: has('guitar'),
  }
}

/**
 * The one pitch frame for the screen (Bowie): every chord is a guitar shape, or every chord is concert
 * pitch, never both side by side. The `-bass` views add the bass note: a hint taken from the shape itself,
 * or the large concert bass note with the concert chord small beside it.
 */
export type PitchView = 'as-written' | 'shapes' | 'shapes-bass' | 'concert' | 'concert-bass'

/** The performer's choice for a song with a capo, when Guitar is among their parts. */
export type PitchFrame = 'shapes' | 'concert'

/** How the song's chords relate to the pitch the band sounds. */
export interface PerformancePitch {
  /** Capo fret; 0 when the song has none. */
  capo: number
  chordsWrittenAs: 'shapes' | 'concert'
  /** `song.scale`: the key as charted. */
  writtenKey?: Scale
}

/** Is the Shapes | Concert toggle offered? Only with a capo, and only to someone playing Guitar. */
export const hasFrameChoice = (parts: readonly PerformancePart[], capo: number) => capo > 0 && parts.includes('guitar')

/**
 * The pitch frame for the parts. Full chart is as written. With a capo and Guitar, the person's frame
 * decides (Shapes when unset); a stored frame means nothing without Guitar. Bass reads concert with its
 * bass note; Keys, and "Show chords" for Vocals or Drums, read concert. Guitar at capo 0 is as written:
 * its shapes are the concert chords.
 */
export const pitchView = (
  parts: readonly PerformancePart[],
  input: { capo: number; frame: PitchFrame | undefined; showChords: boolean }
): PitchView => {
  if (parts.includes('full')) {
    return 'as-written'
  }
  const bass = parts.includes('bass')
  if (hasFrameChoice(parts, input.capo)) {
    if (input.frame === 'concert') {
      return bass ? 'concert-bass' : 'concert'
    }
    return bass ? 'shapes-bass' : 'shapes'
  }
  if (bass) {
    return 'concert-bass'
  }
  if (parts.includes('keys')) {
    return 'concert'
  }
  if (parts.includes('guitar')) {
    return 'as-written'
  }
  return input.showChords ? 'concert' : 'as-written'
}

export type NotesPlacement = 'beside' | 'panel'

/** A request to write a note (N, "+ Jot"). `sectionId: null` writes a song note, anchored to nothing. */
export interface NoteComposeRequest {
  sectionId: string | null
  /** `inline` opens the composer beside the section; `sheet` opens the Jot sheet. */
  surface: 'inline' | 'sheet'
}

/** The performer's own notes in Perform. Private, never tagged by part. */
export interface PerformanceNotesView {
  visible: boolean
  /** Where the notes render. `beside` is the margin on wide screens and inline under each section label on phones. */
  placement: NotesPlacement
  compose: NoteComposeRequest | null
  clearCompose: () => void
  /** Above the song title: where song notes (unanchored or orphaned) and the margin heading render. */
  songNotesSlot: HTMLElement | null
}

export interface PerformanceViewValue {
  parts: PerformancePart[]
  features: PartFeatures
  pitch: PerformancePitch
  /** The one pitch frame for this screen. */
  view: PitchView
  notes: PerformanceNotesView
}

/** Set by the performance views around the song; `null` everywhere else (the editor, previews). */
export const PerformanceViewContext = createContext<PerformanceViewValue | null>(null)

export const usePerformanceView = () => useContext(PerformanceViewContext)
